import { supabase } from '@/integrations/supabase/client';

export interface UserRoleData {
  users: number;
  staff: number;
  admin: number;
}

export interface CourseStatusData {
  notStarted: number;
  inProgress: number;
  completed: number;
}

export interface TopCourse {
  name: string;
  students: number;
}

export interface CategoryCompletion {
  category: string;
  enrollments: number;
  completions: number;
}

export interface CertificateTrend {
  month: string;
  certificates: number;
}

export interface ProgressTrendData {
  date: string;
  progress: number;
  completions: number;
}

export const staffAnalyticsDataService = {
  /**
   * Get real user role distribution from database
   */
  async getUserRoleDistribution(): Promise<UserRoleData> {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role');

      if (error) throw error;

      const distribution = {
        users: 0,
        staff: 0,
        admin: 0
      };

      data?.forEach(row => {
        const role = row.role as string;
        if (role === 'user') distribution.users++;
        else if (role === 'staff') distribution.staff++;
        else if (role === 'admin') distribution.admin++;
      });

      // Count users without roles (they're regular users)
      const { count: profileCount } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      const totalWithRoles = distribution.users + distribution.staff + distribution.admin;
      distribution.users += Math.max(0, (profileCount || 0) - totalWithRoles);

      return distribution;
    } catch (error) {
      console.error('Error fetching user role distribution:', error);
      return { users: 0, staff: 0, admin: 0 };
    }
  },

  /**
   * Get real course status distribution
   */
  async getCourseStatusDistribution(): Promise<CourseStatusData> {
    try {
      const { data, error } = await supabase
        .from('course_assignments')
        .select('status, assignment_status, final_grade');

      if (error) throw error;

      const distribution = {
        notStarted: 0,
        inProgress: 0,
        completed: 0
      };

      data?.forEach(assignment => {
        if (assignment.status === 'completed') {
          distribution.completed++;
        } else if (assignment.final_grade === 0 || assignment.final_grade === null) {
          distribution.notStarted++;
        } else {
          distribution.inProgress++;
        }
      });

      return distribution;
    } catch (error) {
      console.error('Error fetching course status distribution:', error);
      return { notStarted: 0, inProgress: 0, completed: 0 };
    }
  },

  /**
   * Get top courses by student enrollment
   */
  async getTopCourses(limit: number = 5): Promise<TopCourse[]> {
    try {
      const { data, error } = await supabase
        .from('course_assignments')
        .select('course_id');

      if (error) throw error;

      // Count enrollments per course
      const courseCounts = new Map<string, number>();
      data?.forEach(assignment => {
        const count = courseCounts.get(assignment.course_id) || 0;
        courseCounts.set(assignment.course_id, count + 1);
      });

      // Convert to array and sort by count
      const topCourses = Array.from(courseCounts.entries())
        .map(([courseId, count]) => ({
          name: courseId.split('-').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' '),
          students: count
        }))
        .sort((a, b) => b.students - a.students)
        .slice(0, limit);

      return topCourses;
    } catch (error) {
      console.error('Error fetching top courses:', error);
      return [];
    }
  },

  /**
   * Get course completion data by category
   */
  async getCategoryCompletions(): Promise<CategoryCompletion[]> {
    try {
      // Get all courses with their categories
      const { data: courses, error: coursesError } = await supabase
        .from('courses')
        .select('id, category');

      if (coursesError) throw coursesError;

      // Get all assignments
      const { data: assignments, error: assignmentsError } = await supabase
        .from('course_assignments')
        .select('course_id, status');

      if (assignmentsError) throw assignmentsError;

      // Map course_id to category
      const courseCategories = new Map<string, string>();
      courses?.forEach(course => {
        courseCategories.set(course.id, course.category || 'General');
      });

      // Count by category
      const categoryStats = new Map<string, { enrollments: number; completions: number }>();
      
      assignments?.forEach(assignment => {
        const category = courseCategories.get(assignment.course_id) || 'General';
        const stats = categoryStats.get(category) || { enrollments: 0, completions: 0 };
        
        stats.enrollments++;
        if (assignment.status === 'completed') {
          stats.completions++;
        }
        
        categoryStats.set(category, stats);
      });

      // Convert to array and sort by enrollments
      return Array.from(categoryStats.entries())
        .map(([category, stats]) => ({
          category,
          enrollments: stats.enrollments,
          completions: stats.completions
        }))
        .sort((a, b) => b.enrollments - a.enrollments)
        .slice(0, 10);
    } catch (error) {
      console.error('Error fetching category completions:', error);
      return [];
    }
  },

  /**
   * Get certificate issuance trend (last 6 months)
   */
  async getCertificateTrend(): Promise<CertificateTrend[]> {
    try {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

      const { data, error } = await supabase
        .from('certificates')
        .select('issue_date')
        .gte('issue_date', sixMonthsAgo.toISOString());

      if (error) throw error;

      // Group by month
      const monthCounts = new Map<string, number>();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      data?.forEach(cert => {
        const date = new Date(cert.issue_date);
        const monthKey = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
        monthCounts.set(monthKey, (monthCounts.get(monthKey) || 0) + 1);
      });

      // Fill in missing months with 0
      const trend: CertificateTrend[] = [];
      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const monthKey = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
        trend.push({
          month: monthNames[date.getMonth()],
          certificates: monthCounts.get(monthKey) || 0
        });
      }

      return trend;
    } catch (error) {
      console.error('Error fetching certificate trend:', error);
      return [];
    }
  },

  /**
   * Get progress trend over specified days
   */
  async getProgressTrend(days: number = 30): Promise<ProgressTrendData[]> {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      // Get module activity logs for progress
      const { data: activityData, error: activityError } = await supabase
        .from('module_activity_logs')
        .select('created_at, activity_type')
        .gte('created_at', startDate.toISOString());

      if (activityError) throw activityError;

      // Get course completions
      const { data: completionData, error: completionError } = await supabase
        .from('course_assignments')
        .select('completion_date, final_grade')
        .gte('completion_date', startDate.toISOString())
        .eq('status', 'completed');

      if (completionError) throw completionError;

      // Group by day
      const dailyStats = new Map<string, { activities: number; completions: number }>();
      
      activityData?.forEach(activity => {
        const dateKey = new Date(activity.created_at).toLocaleDateString();
        const stats = dailyStats.get(dateKey) || { activities: 0, completions: 0 };
        stats.activities++;
        dailyStats.set(dateKey, stats);
      });

      completionData?.forEach(completion => {
        if (completion.completion_date) {
          const dateKey = new Date(completion.completion_date).toLocaleDateString();
          const stats = dailyStats.get(dateKey) || { activities: 0, completions: 0 };
          stats.completions++;
          dailyStats.set(dateKey, stats);
        }
      });

      // Get sample of recent data points (last 7 days)
      const trend: ProgressTrendData[] = [];
      const intervals = [30, 21, 14, 7, 3, 1, 0];
      
      intervals.forEach(daysAgo => {
        const date = new Date();
        date.setDate(date.getDate() - daysAgo);
        const dateKey = date.toLocaleDateString();
        const stats = dailyStats.get(dateKey) || { activities: 0, completions: 0 };
        
        const label = daysAgo === 0 ? 'Today' : 
                     daysAgo === 1 ? 'Yesterday' :
                     `${daysAgo} days ago`;
        
        trend.push({
          date: label,
          progress: Math.min(100, stats.activities * 5), // Rough progress estimate
          completions: stats.completions
        });
      });

      return trend;
    } catch (error) {
      console.error('Error fetching progress trend:', error);
      return [];
    }
  }
};
